import React, { useState, useEffect, useRef } from "react";
import { User, LogOut, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API_ROOT } from "@/config";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface CollapsedProfileProps {
  className?: string;
  style?: React.CSSProperties;
}

const CollapsedProfile: React.FC<CollapsedProfileProps> = ({ className = "", style = {} }) => {
  const [currentUser, setCurrentUser] = useState<{ first_name: string; surname: string; email: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fetch current user data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${API_ROOT}/users/me`, {
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("token")}`
          }
        });
        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCurrentUser();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get user initials
  const getUserInitials = (first_name: string, surname: string, email: string) => {
    if (first_name && surname) {
      return (first_name[0] + surname[0]).toUpperCase();
    } else if (first_name) {
      return first_name.substring(0, 2).toUpperCase();
    } else {
      // Fallback to email if no name
      return email.substring(0, 2).toUpperCase();
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/auth");
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_ROOT}/users/me`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });

      if (response.ok) {
        // Clear token and redirect to auth
        localStorage.removeItem("token");
        navigate("/auth");
      } else {
        throw new Error("Failed to delete account");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("Failed to delete account. Please try again.");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <>
      <div className={`flex flex-col items-center relative ${className}`} style={style} ref={userDropdownRef}>
        <div
          onClick={() => setUserDropdownOpen(!userDropdownOpen)}
          className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all hover:opacity-80"
          style={{ 
            background: PALETTE.paleViolet,
            color: PALETTE.brightSnow
          }}
          title={currentUser ? `${currentUser.first_name} ${currentUser.surname}` || currentUser.email : "User Profile"}
        >
          {isLoading ? (
            // Show nothing while loading to prevent icon flash
            <div className="w-4 h-4" />
          ) : currentUser ? (
            <span className="text-xs font-semibold">
              {getUserInitials(currentUser.first_name, currentUser.surname, currentUser.email)}
            </span>
          ) : (
            <User className="w-4 h-4" />
          )}
        </div>

        {/* Dropdown Menu */}
        {userDropdownOpen && currentUser && (
          <div 
            className="absolute bottom-full left-full ml-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
            style={{ background: PALETTE.brightSnow }}
          >
            {/* User Email */}
            <div className="px-4 py-2 border-b border-gray-200">
              <p className="text-sm font-medium" style={{ color: PALETTE.gunmetal }}>
                {currentUser.email}
              </p>
              <p className="text-xs text-gray-500">
                {currentUser.first_name} {currentUser.surname}
              </p>
            </div>

            {/* Delete Account */}
            <div 
              className="px-4 py-2 hover:bg-red-50 cursor-pointer transition-colors"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <div className="flex items-center gap-3">
                <Trash2 className="w-4 h-4" style={{ color: PALETTE.orange }} />
                <span className="text-sm" style={{ color: PALETTE.orange }}>
                  Delete Account
                </span>
              </div>
            </div>

            {/* Logout */}
            <div 
              className="px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors border-t border-gray-200"
              onClick={handleLogout}
            >
              <div className="flex items-center gap-3">
                <LogOut className="w-4 h-4" style={{ color: PALETTE.gunmetal }} />
                <span className="text-sm" style={{ color: PALETTE.gunmetal }}>
                  Logout
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center" style={{ color: PALETTE.orange }}>
              Delete Account
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center">
              <Trash2 className="w-12 h-12 mx-auto mb-4" style={{ color: PALETTE.orange }} />
              <p className="text-base mb-2" style={{ color: PALETTE.gunmetal }}>
                Are you sure you want to delete your account?
              </p>
              <p className="text-sm text-gray-600">
                This action will permanently delete:
              </p>
              <ul className="text-sm text-gray-600 mt-2 text-left">
                <li>• Your account and profile</li>
                <li>• All your projects and analyses</li>
                <li>• All uploaded videos and results</li>
                <li>• This action cannot be undone</li>
              </ul>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 rounded-lg"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 rounded-lg"
                style={{ 
                  background: PALETTE.orange, 
                  color: PALETTE.brightSnow,
                  border: 'none'
                }}
                onClick={handleDeleteAccount}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Account"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CollapsedProfile; 